import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Task as TaskIcon,
  StickyNote2 as NoteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useProject, useProjectTasks, useProjectNotes } from '../../hooks/useProjects';
import { useProjectMutations } from '../../hooks/useProjectMutations';
import { ProjectStatusBadge, ProjectDetailModal } from '../../components/projects';
import { TaskItem } from '../../components/tasks/TaskItem';
import { NoteCard } from '../../components/notes/NoteCard';
import type { UpdateProjectRequest } from '../../types/project';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data: projectData, isLoading: projectLoading } = useProject(id || '');
  const { data: tasksData, isLoading: tasksLoading } = useProjectTasks(id || '');
  const { data: notesData, isLoading: notesLoading } = useProjectNotes(id || '');
  const mutations = useProjectMutations();

  const project = projectData?.data?.project;
  const tasks = tasksData?.data?.tasks || [];
  const notes = notesData?.data?.notes || [];

  const handleUpdateProject = async (data: UpdateProjectRequest) => {
    if (!id) return;

    try {
      await mutations.updateProject.mutateAsync({ id, data });
      setSnackbar({
        open: true,
        message: 'Project updated successfully',
        severity: 'success',
      });
      setIsEditModalOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to update project',
        severity: 'error',
      });
    }
  };

  if (projectLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!project) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6">Project not found</Typography>
        <Button onClick={() => navigate('/projects')} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  const clientName = project.clientId
    ? `${project.clientId.firstName} ${project.clientId.lastName}`
    : null;

  const progress = project.progress || 0;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink
          component={Link}
          to="/projects"
          color="inherit"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Projects
        </MuiLink>
        <Typography color="text.primary">{project.name}</Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/projects')}
        sx={{ mb: 2 }}
      >
        Back to Projects
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Stack direction="row" spacing={2} alignItems="center" mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {project.name}
              </Typography>
              <ProjectStatusBadge status={project.status} />
            </Stack>

            {project.description && (
              <Typography variant="body1" color="text.secondary" mb={2}>
                {project.description}
              </Typography>
            )}

            <Stack direction="row" spacing={3} flexWrap="wrap">
              {clientName && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">{clientName}</Typography>
                </Stack>
              )}
              {project.startDate && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Started: {format(new Date(project.startDate), 'MMM d, yyyy')}
                  </Typography>
                </Stack>
              )}
              {project.dueDate && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Due: {format(new Date(project.dueDate), 'MMM d, yyyy')}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {/* Tags */}
            {project.tags.length > 0 && (
              <Stack direction="row" spacing={0.5} mt={2}>
                {project.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
          </Box>

          <IconButton onClick={() => setIsEditModalOpen(true)}>
            <EditIcon />
          </IconButton>
        </Stack>

        {/* Progress */}
        <Box mt={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Progress</Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {progress}% ({project.completedTaskCount || 0}/{project.taskCount || 0} tasks)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: progress === 100 ? 'success.main' : 'primary.main',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <TaskIcon fontSize="small" />
                <span>Tasks ({tasks.length})</span>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <NoteIcon fontSize="small" />
                <span>Notes ({notes.length})</span>
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {/* Tasks Tab */}
      <TabPanel value={tabValue} index={0}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Tasks</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => navigate(`/tasks?projectId=${id}`)}
          >
            Add Task
          </Button>
        </Stack>

        {tasksLoading ? (
          <Typography>Loading tasks...</Typography>
        ) : tasks.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <TaskIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No tasks yet</Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {tasks.map((task: any) => (
              <Paper key={task._id} sx={{ p: 1 }}>
                <TaskItem
                  task={task}
                  onToggleComplete={() => {}}
                  onEdit={() => navigate(`/tasks?taskId=${task._id}`)}
                  onDelete={() => {}}
                />
              </Paper>
            ))}
          </Stack>
        )}
      </TabPanel>

      {/* Notes Tab */}
      <TabPanel value={tabValue} index={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Notes</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => navigate(`/notes?projectId=${id}`)}
          >
            Add Note
          </Button>
        </Stack>

        {notesLoading ? (
          <Typography>Loading notes...</Typography>
        ) : notes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notes yet</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {notes.map((note: any) => (
              <Grid item xs={12} sm={6} md={4} key={note._id}>
                <NoteCard
                  note={note}
                  onEdit={() => navigate(`/notes?noteId=${note._id}`)}
                  onDelete={() => {}}
                  onPin={() => {}}
                  onArchive={() => {}}
                  onConvertToTask={() => {}}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Edit Modal */}
      <ProjectDetailModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        project={project}
        onSave={handleUpdateProject}
        isLoading={mutations.updateProject.isPending}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDetailPage;
